import { useQuery } from '@tanstack/react-query';
import { Divider, Flex, List, Typography } from 'antd';
import { Fragment, useContext, useEffect, useState } from 'react';
import { getApprovalProcessDetail } from 'src/apis/master_data/approvalProcess.api';
import Approved from 'src/assets/icons/approved.png';
import Created from 'src/assets/icons/created.png';
import Rejected from 'src/assets/icons/rejected.png';
import Waiting from 'src/assets/icons/waiting.png';
import Icon from 'src/components/v2/Icon';
import { TicketDetailContext } from 'src/contexts/ticketDetail.context';
import { TicketStatus, ApprovalStatus } from 'src/types/helpdesk/ticket.type';
import { IApprovalProcessDetail, IAssignLog, IProcessTree, StatusType } from 'src/types/helpdesk/processTree.type';
import { formatDate, formatTime } from 'src/utils/utils';
import './ProcessTree.scss';
import Link from 'antd/es/typography/Link';

const { Text } = Typography;

interface IconType {
  status: StatusType;
  size?: number;
  className?: string;
}

const RenderIcon = ({ status, size = 16, className }: IconType) => {
  switch (status) {
    case 'approved':
      return <Icon className={className} url={Approved} size={size} titleTooltip='Đã duyệt' />;
    case 'accepted':
      return <Icon className={className} url={Approved} size={size} titleTooltip='Đã tiếp nhận' />;
    case 'waiting':
      return <Icon className={className} url={Waiting} size={size} titleTooltip='Đang đợi' />;
    case 'rejected':
      return <Icon className={className} url={Rejected} size={size} titleTooltip='Đã từ chối' />;
    case 'canceled':
      return <Icon className={className} url={Rejected} size={size} titleTooltip='Đã hủy ticket' />;
    case 'created':
      return <Icon className={className} url={Created} size={size} titleTooltip='Tạo mới thành công' />;
    case 'confirmedHandle':
      return <Icon className={className} url={Approved} size={size} titleTooltip='Đã xác nhận hoàn thành' />;
    case 'finished':
      return <Icon className={className} url={Approved} size={size} titleTooltip='Đã hoàn thành' />;
  }
};

const ProcessTree = () => {
  const { ticket, loading } = useContext(TicketDetailContext);
  const [processTree, setProcessTree] = useState<IProcessTree[]>([]);

  const approvalProcessDetailQuery = useQuery({
    queryKey: [
      'approvalProcessDetail',
      ticket?.data?.approvalProcessId,
      ticket?.data?.versionOfApproval,
      ticket?.data?.createdBy
    ],
    queryFn: () =>
      getApprovalProcessDetail(
        ticket?.data?.approvalProcessId as number,
        ticket?.data?.versionOfApproval as number,
        ticket?.data?.createdBy as number
      ),
    enabled: !!ticket?.data?.approvalProcessId && !!ticket?.data?.versionOfApproval && !!ticket?.data?.createdBy
  });

  useEffect(() => {
    if (!!ticket?.data && approvalProcessDetailQuery.data?.data && approvalProcessDetailQuery.data.data.length > 0) {
      const newProcessTree: IProcessTree[] = [];

      // Tạo ticket
      const creatorObj: IProcessTree = {
        title: 'Tạo ticket',
        type: 'create',
        ticketCategoryName: ticket.data.ticketCategory?.name,
        createdEmployee: ticket.data.createdByObj,
        createdDate: ticket.data.createdDate
      };
      newProcessTree.push(creatorObj);

      // Phê duyệt ticket
      let convertApprovalProcessDetail: IApprovalProcessDetail[] = [];

      approvalProcessDetailQuery.data.data?.forEach((item) => {
        const index = convertApprovalProcessDetail.findIndex(
          (itemConvert) => itemConvert.approvalLevel === item.approvalLevel
        );
        if (index === -1) {
          item.employee &&
            convertApprovalProcessDetail.push({
              approvalLevel: item.approvalLevel,
              stepName: item.stepName,
              employeeId: undefined,
              position: undefined,
              approval: undefined,
              employees: [item.employee]
            } as IApprovalProcessDetail);

          item.position &&
            convertApprovalProcessDetail.push({
              ...item,
              approvalLevel: item.approvalLevel,
              stepName: item.stepName,
              employeeId: undefined,
              position: item.position,
              approval: undefined,
              employees: []
            } as IApprovalProcessDetail);
        } else {
          convertApprovalProcessDetail[index].employees?.push(item.employee);
        }
      });

      convertApprovalProcessDetail = convertApprovalProcessDetail.sort(
        (itemX, itemY) => itemX.approvalLevel - itemY.approvalLevel
      );

      convertApprovalProcessDetail = convertApprovalProcessDetail?.map((item) => {
        const findItemApproval = ticket?.data.approvalLogs.find(
          (approval) => approval.step === item.approvalLevel && approval.stepName === item.stepName
        );

        if (findItemApproval) {
          return {
            ...item,
            approval: findItemApproval,
            statusType: findItemApproval.approvalStatus === 1 ? 'approved' : 'rejected'
          };
        } else {
          return {
            ...item
          };
        }
      });

      const approvalObj: IProcessTree = {
        title: 'Duyệt',
        type: 'approve',
        approvalLog: convertApprovalProcessDetail
      };
      newProcessTree.push(approvalObj);

      // Tiếp nhận ticket
      const accepterObj: IProcessTree = {
        title: 'Tiếp nhận',
        type: 'accept',
        acceptLog: {
          statusType: 'waiting',
          employees: ticket.data.accepts?.map((item) => ({
            id: item?.id,
            code: item?.code,
            name: item?.name
          }))
        }
      };

      if (ticket.data.handleLogs.length > 0) {
        accepterObj.acceptLog = {
          ...accepterObj.acceptLog,
          approvalStatus: ticket.data.handleLogs[0].approvalStatus,
          reason: ticket.data.handleLogs[0].reason,
          logAt: ticket.data.handleLogs[0].logAt,
          employee: ticket.data.handleLogs[0].employee,
          createdDate: ticket.data.handleLogs[0].createdDate,
          statusType: ticket.data.handleLogs[0].approvalStatus === 1 ? 'accepted' : 'rejected'
        };
      }
      newProcessTree.push(accepterObj);

      // Xử lý ticket
      const handleObj: IProcessTree = {
        title: 'Xử lý',
        type: 'handle',
        assignLog: [
          {
            statusType: 'waiting',
            assigners: ticket.data.assigns?.map((item) => ({
              id: item?.id,
              code: item?.code,
              name: item?.name
            }))
          }
        ]
      };

      const assignLogs: IAssignLog[] | undefined = [...(ticket.data.assignLogs ?? [])];

      if (assignLogs && assignLogs?.length > 0) {
        handleObj.assignLog = assignLogs?.map((item, index, data) => ({
          ...item,
          assigners: handleObj.assignLog?.[0].assigners,
          statusType:
            index < data.length - 1
              ? 'approved'
              : item.isNotConfirm || ticket.data.ticketStatus?.id >= TicketStatus.HandleCompleted
              ? 'approved'
              : item.noteReject
              ? 'rejected'
              : 'waiting',
          noteAt: item.noteAt
        }));
      }

      if (handleObj?.assignLog) {
        const lastAssignLog = handleObj?.assignLog[handleObj?.assignLog?.length - 1];
        const check = lastAssignLog.isNotConfirm;

        if (check) {
          handleObj.assignLog.push({
            ...lastAssignLog,
            assigner: undefined,
            isNotConfirm: false,
            isCurrent: false,
            remark: undefined,
            logAt: undefined,
            statusType: 'waiting',
            noteAt: undefined
          });
        }
        newProcessTree.push(handleObj);
      }

      // Hoàn thành ticket
      const doneObj: IProcessTree = {
        title: 'Hoàn thành',
        type: 'done'
      };

      newProcessTree.push(doneObj);

      setProcessTree(newProcessTree);
    }
  }, [ticket?.data, approvalProcessDetailQuery.data?.data]);

  const RenderItem = (item: IProcessTree, index: number) => {
    if (item.type === 'create') {
      return (
        <List.Item className='process-tree-item' key={'create'}>
          <List.Item.Meta
            avatar={
              <Flex className='item-left' vertical align='center' justify='start'>
                <Text strong>{formatTime(item.createdDate)}</Text>
                <Text strong className='date'>
                  {formatDate(item.createdDate)}
                </Text>
                <Flex className='icon-wrapper'>
                  <RenderIcon status='created' className='icon-status' />
                </Flex>
              </Flex>
            }
            title={<Text strong>{item.title}</Text>}
            description={<Text key={'create-description'}>{item.createdEmployee?.fullName}</Text>}
          />
        </List.Item>
      );
    }

    if (item.type === 'approve') {
      return item.approvalLog?.map((itemV2) => (
        <List.Item className='process-tree-item' key={`approve-${itemV2.approvalLevel}`}>
          <List.Item.Meta
            avatar={
              <Flex className='item-left' vertical align='center' justify='start'>
                <Text strong>{formatTime(itemV2.approval?.approvedAt)}</Text>
                <Text strong className='date'>
                  {formatDate(itemV2.approval?.approvedAt)}
                </Text>
                <Flex className='icon-wrapper'>
                  <RenderIcon status={itemV2.statusType ?? 'waiting'} className='icon-status' />
                </Flex>
              </Flex>
            }
            title={<Text strong>{`${item.title}: ${itemV2.stepName}`}</Text>}
            description={
              <Flex vertical gap={5} key={'approve-description'}>
                {!!itemV2.approval ? (
                  <Text>{itemV2.approval.employee?.fullName}</Text>
                ) : itemV2?.employees ? (
                  itemV2.employees?.map((employee) => <Text key={employee?.code}>{employee?.fullName}</Text>)
                ) : (
                  ''
                )}
                {itemV2.approval?.approvalStatus === ApprovalStatus.Reject && (
                  <Text>
                    <Text className='title'>Lý do: </Text>
                    <Text italic>{ticket?.data?.reason?.name.concat('. ', itemV2.approval?.noteReject ?? '')}</Text>
                  </Text>
                )}
                {itemV2.approval?.approvalStatus === ApprovalStatus.Approve && itemV2.approval?.noteApprove && (
                  <Text>
                    <Text className='title'>Ghi chú: </Text>
                    <Text italic>{itemV2.approval?.noteApprove}</Text>
                  </Text>
                )}
              </Flex>
            }
          />
        </List.Item>
      ));
    }

    if (item.type === 'accept') {
      return (
        <List.Item className='process-tree-item' key={'accept'}>
          <List.Item.Meta
            avatar={
              <Flex className='item-left' vertical align='center' justify='start'>
                <Text strong>{formatTime(item.acceptLog?.logAt)}</Text>
                <Text strong className='date'>
                  {formatDate(item.acceptLog?.logAt)}
                </Text>
                <Flex className='icon-wrapper'>
                  <RenderIcon status={item.acceptLog?.statusType ?? 'waiting'} className='icon-status' />
                </Flex>
              </Flex>
            }
            title={<Text strong>{item.title}</Text>}
            description={
              <Flex vertical gap={5} key={'accept-description'}>
                {!!item.acceptLog?.employee ? (
                  <Text>{item.acceptLog.employee?.fullName}</Text>
                ) : (
                  item.acceptLog?.employees?.map((employee, index) => <Text key={index}>{employee?.name}</Text>)
                )}
                {item.acceptLog?.approvalStatus === ApprovalStatus.Reject && (
                  <Text>
                    <Text className='title'>Lý do: </Text>
                    <Text italic>{ticket?.data.reason?.name.concat('. ', ticket?.data.noteReject ?? '')}</Text>
                  </Text>
                )}
              </Flex>
            }
          />
        </List.Item>
      );
    }

    if (item.type === 'handle') {
      const itemList = item.assignLog?.map((itemV2, index) => {
        return (
          <Fragment key={'handle'}>
            <List.Item className='process-tree-item' key={'assign-handle'}>
              <List.Item.Meta
                avatar={
                  <Flex className='item-left' vertical align='center' justify='start'>
                    <Text strong>{formatTime(itemV2.logAt)}</Text>
                    <Text strong className='date'>
                      {formatDate(itemV2.logAt)}
                    </Text>
                    <Flex className='icon-wrapper'>
                      <RenderIcon status={itemV2.statusType ?? 'waiting'} className='icon-status' />
                    </Flex>
                  </Flex>
                }
                title={
                  <Flex justify='start' gap={5}>
                    <Text strong>{item.title}:</Text>
                    {!!itemV2.assigner?.code && <Text>{`Lần ${index + 1}`}</Text>}
                  </Flex>
                }
                description={
                  <Flex vertical gap={5} key={'handle-description'}>
                    {!!itemV2.assigner ? (
                      <Flex vertical gap={8}>
                        {itemV2.assigner?.fullName && (
                          <Text>
                            <Text type='secondary'>Người chỉ định: </Text>
                            {itemV2.assigner?.fullName}
                          </Text>
                        )}
                        {itemV2.remark && (
                          <Text>
                            <Text type='secondary'>Ghi chú của người chỉ định: </Text>
                            {itemV2.remark}
                          </Text>
                        )}
                        {itemV2.handler?.fullName && (
                          <Text>
                            <Text type='secondary'>Người xử lý: </Text>
                            {itemV2.handler?.fullName}
                          </Text>
                        )}
                        {ticket?.data.ticketSolutions?.find((item) => item.isApproval)?.title && (
                          <Text>
                            <Text type='secondary'>Giải pháp: </Text>
                            <Link>{ticket?.data.ticketSolutions?.find((item) => item.isApproval)?.title}</Link>
                          </Text>
                        )}
                        {itemV2.noteComplete && (
                          <Text>
                            <Text type='secondary'>Ghi chú hoàn thành: </Text>
                            <Text type='success'>{itemV2.noteComplete}</Text>
                          </Text>
                        )}
                        {itemV2.noteReject && (
                          <Text>
                            <Text type='secondary'>Lý do từ chối xử lý: </Text>
                            <Text type='danger'>{itemV2.noteReject}</Text>
                          </Text>
                        )}
                      </Flex>
                    ) : (
                      itemV2.assigners?.map((item, index) => <Text key={index}>{item?.name}</Text>)
                    )}
                  </Flex>
                }
              />
            </List.Item>
            <List.Item className='process-tree-item' key={'completed-handle'}>
              <List.Item.Meta
                avatar={
                  <Flex className='item-left' vertical align='center' justify='start'>
                    <Text strong>{formatTime(itemV2.noteAt)}</Text>
                    <Text strong className='date'>
                      {formatDate(itemV2.noteAt)}
                    </Text>
                    <Flex className='icon-wrapper'>
                      <RenderIcon
                        status={
                          itemV2.isNotConfirm
                            ? 'rejected'
                            : ticket?.data.ticketStatus?.id === TicketStatus.Finished
                            ? 'confirmedHandle'
                            : 'waiting'
                        }
                        className='icon-status'
                      />
                    </Flex>
                  </Flex>
                }
                title={<Text strong>Xác nhận đã xử lý</Text>}
                description={
                  <Flex gap={5} vertical>
                    <Text>{ticket?.data?.createdByObj?.name}</Text>
                    {itemV2.isNotConfirm && (
                      <Text>
                        <Text className='title'>Lý do: </Text>
                        <Text italic>{itemV2.noteReject}</Text>
                      </Text>
                    )}
                    {/* {ticket?.data.ticketStatus?.id === TicketStatus.Finished && itemV2.isNotConfirm === false && (
                      <Text>
                        <Text className='title'>Nội dung: </Text>
                        <Text italic>{(itemV2.noteComplete ?? '')}</Text>
                      </Text>
                    )} */}
                  </Flex>
                }
              />
            </List.Item>
          </Fragment>
        );
      });

      return itemList;
    }

    if (item.type === 'done') {
      return (
        <List.Item className='process-tree-item' key={'done'}>
          <List.Item.Meta
            avatar={
              <Flex className='item-left' vertical align='center' justify='start'>
                {ticket?.data.ticketStatus?.id === TicketStatus.Finished && (
                  <>
                    <Text strong>{formatTime(ticket?.data.finishedDate)}</Text>
                    <Text strong className='date'>
                      {formatDate(ticket?.data.finishedDate)}
                    </Text>
                  </>
                )}

                {ticket?.data.ticketStatus?.id === TicketStatus.Canceled && (
                  <>
                    <Text strong>{formatTime(ticket?.data.lastModifiedDate)}</Text>
                    <Text strong className='date'>
                      {formatDate(ticket?.data.lastModifiedDate)}
                    </Text>
                  </>
                )}
                <Flex className='icon-wrapper'>
                  <RenderIcon
                    status={
                      ticket?.data.ticketStatus?.id === TicketStatus.Finished
                        ? 'finished'
                        : ticket?.data.ticketStatus?.id === TicketStatus.Canceled
                        ? 'canceled'
                        : 'waiting'
                    }
                    className='icon-status'
                  />
                </Flex>
              </Flex>
            }
            title={
              ticket?.data.ticketStatus?.id === TicketStatus.Canceled ? (
                <Text strong>Hủy ticket</Text>
              ) : (
                <Text strong>Hoàn thành</Text>
              )
            }
            description={ticket?.data.ticketStatus?.id === TicketStatus.Canceled && <Text>NGƯỜI TẠO TICKET</Text>}
          />
        </List.Item>
      );
    }
  };

  return (
    <Flex className='process-tree' vertical>
      <Divider style={{ marginTop: '8px' }} orientation='left'>
        <Text strong>Hoạt động chính</Text>
      </Divider>
      <List
        className='process-tree-list'
        dataSource={processTree}
        renderItem={RenderItem}
        loading={loading || approvalProcessDetailQuery.isFetching}
      />
    </Flex>
  );
};

export default ProcessTree;
